import React from "react";
import { Modal, Box, Typography, Button, makeStyles } from "@material-ui/core";
import { WarningRounded as WarningIcon } from "@material-ui/icons";

/**
 * 결제 실패 → 재시도 대기 중인 구독을 다시 구독하려 할 때 띄우는 안내 (2026-10-30).
 *
 * 새로 구독하면 밀린 재시도까지 두 번 결제되므로 서버가 새 구독을 막는다(409 PAYMENT_RETRY_PENDING).
 * 고객은 여기서 지금 등록된 카드로 바로 결제하거나, 결제수단을 바꿔(바꾸면 밀린 결제가 새 카드로 바로 결제됨) 해결한다.
 */
export interface RetryPayment {
  history_id: number;
  product_id: number;
  product_name: string;
  is_yearly: boolean;
  amount: number;
  failed_reason: string;
  failed_at: string | null;
  retry_at: string | null;
}

export interface RetryPendingData {
  message?: string;
  retry_payments: RetryPayment[];
  card?: { last4?: string; name?: string } | null;
}

interface Props {
  open: boolean;
  data: RetryPendingData | null;
  loadingId: number | null;
  onClose: () => void;
  onRetryNow: (historyId: number) => void;
  onChangeCard: () => void;
}

const useStyles = makeStyles((theme) => ({
  modal: { display: "flex", alignItems: "center", justifyContent: "center" },
  paper: {
    width: "90%",
    maxWidth: 520,
    maxHeight: "90vh",
    overflow: "auto",
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    boxShadow: theme.shadows[24],
    padding: theme.spacing(4),
    outline: "none",
    [theme.breakpoints.down("sm")]: { width: "95%", padding: theme.spacing(2) },
  },
  header: { display: "flex", alignItems: "center", marginBottom: theme.spacing(2) },
  icon: { fontSize: 40, marginRight: theme.spacing(1.5), color: "#ff9800" },
  item: {
    border: "1px solid #ffe0b2",
    background: "#fff8e1",
    borderRadius: 6,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
  },
  row: { display: "flex", justifyContent: "space-between", gap: theme.spacing(2), marginTop: 4 },
  label: { color: "#666", flexShrink: 0 },
  buttons: { display: "flex", flexDirection: "column", gap: theme.spacing(1), marginTop: theme.spacing(2) },
}));

const won = (n: number) => `${(n || 0).toLocaleString()}원`;

const PaymentRetryPendingModal: React.FC<Props> = ({ open, data, loadingId, onClose, onRetryNow, onChangeCard }) => {
  const classes = useStyles();
  if (!data || !data.retry_payments?.length) return null;
  const last4 = data.card?.last4;
  const busy = loadingId !== null;

  return (
    <Modal open={open} onClose={busy ? undefined : onClose} className={classes.modal}>
      <Box className={classes.paper}>
        <div className={classes.header}>
          <WarningIcon className={classes.icon} />
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            결제가 밀린 구독이 있어요
          </Typography>
        </div>
        <Typography variant="body2" style={{ marginBottom: 16, lineHeight: 1.6 }}>
          지난 결제가 실패해서 다시 결제를 기다리고 있습니다. 지금 새로 구독하면 두 번 결제될 수 있어 새 구독은 막아 두었어요.
          아래에서 바로 결제하면 이용권이 다시 열립니다.
        </Typography>

        {data.retry_payments.map((p) => (
          <div key={p.history_id} className={classes.item}>
            <Typography style={{ fontWeight: 700 }}>
              {p.product_name} {p.is_yearly ? "연간" : "월간"} 구독 · {won(p.amount)}
            </Typography>
            <div className={classes.row}>
              <Typography variant="body2" className={classes.label}>실패 사유</Typography>
              <Typography variant="body2" align="right">{p.failed_reason}</Typography>
            </div>
            {p.retry_at && (
              <div className={classes.row}>
                <Typography variant="body2" className={classes.label}>자동 재결제 예정</Typography>
                <Typography variant="body2" align="right">{p.retry_at}</Typography>
              </div>
            )}
            <div className={classes.buttons}>
              <Button
                variant="contained"
                color="primary"
                disabled={busy}
                onClick={() => onRetryNow(p.history_id)}
              >
                {loadingId === p.history_id
                  ? "결제 중..."
                  : `지금 등록된 카드${last4 ? `(${last4})` : ""}로 결제`}
              </Button>
            </div>
          </div>
        ))}

        <div className={classes.buttons}>
          <Button variant="outlined" color="primary" disabled={busy} onClick={onChangeCard}>
            결제수단 변경 (바꾸면 새 카드로 바로 결제돼요)
          </Button>
          <Button disabled={busy} onClick={onClose}>닫기</Button>
        </div>
      </Box>
    </Modal>
  );
};

export default PaymentRetryPendingModal;
